import React, {useEffect, useState} from 'react';
import ModalContainer from '../../../../uiToolkit/ModalContainer';
import LoadingSpinner from '../../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import TextInput from '../../../../uiToolkit/Inputs/TextInput';
import TextAreaInput from '../../../../uiToolkit/Inputs/TextAreaInput';
import PosterInput, {
  PosterInputText,
} from '../../../../uiToolkit/Inputs/PosterInput';
import Button from '../../../../uiToolkit/Buttons/Button';
import {microreelsListText} from '../microreelsListText';
import styles from './EditMicroreelInfoModal.module.css';

interface Props {
  shown: boolean;
  headerTitle: string;
  initialName?: string;
  initialDescription?: string;
  isLoading: boolean;
  onCancel: () => void;
  onSave: (name: string, description: string, cover?: File) => void;
  namePlaceholder?: string;
  descriptionField?: string;
  descriptionPlaceholder?: string;
  maxDescriptionLength?: number;
  posterText?: PosterInputText;
  posterAspectRatio?: string;
  posterMaxWidth?: number;
  initialCover?: string;
}

const EditMicroreelInfoModal = React.memo(
  ({
    shown,
    headerTitle,
    initialName,
    initialDescription,
    isLoading,
    onCancel,
    onSave,
    namePlaceholder,
    descriptionField,
    descriptionPlaceholder,
    maxDescriptionLength,
    posterText,
    posterAspectRatio,
    posterMaxWidth,
    initialCover,
  }: Props) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [cover, setCover] = useState<File>();

    useEffect(() => {
      if (initialName !== undefined) {
        setTitle(initialName);
        setDescription(initialDescription ?? '');
      }
    }, [initialName, initialDescription]);

    if (!shown) return null;

    const dataLoaded = initialName !== undefined;

    return (
      <ModalContainer onClose={onCancel} className={styles.modal}>
        <div className={styles.container}>
          {isLoading && !dataLoaded ? (
            <div className={styles.loading}>
              <LoadingSpinner size={48} />
              <p className={styles.loadingText}>{headerTitle}</p>
            </div>
          ) : (
            <>
              <h3 className={styles.title}>{headerTitle}</h3>

              <p className={styles.fieldTitle}>
                {microreelsListText.editInfoTitleField}
                <span className="MandatoryIndicator">*</span>
              </p>
              <TextInput
                value={title}
                placeholder={
                  namePlaceholder ?? microreelsListText.editInfoTitlePlaceholder
                }
                onChange={setTitle}
                maxCharacters={40}
              />

              <p className={styles.fieldTitle}>
                {descriptionField ??
                  microreelsListText.editInfoDescriptionField}
              </p>
              <TextAreaInput
                value={description}
                placeholder={
                  descriptionPlaceholder ??
                  microreelsListText.editInfoDescriptionPlaceholder
                }
                onChange={setDescription}
                maxCharacters={maxDescriptionLength ?? 250}
              />

              {posterText && (
                <PosterInput
                  text={posterText}
                  onChange={setCover}
                  image={cover}
                  existingImage={initialCover}
                  aspectRatio={posterAspectRatio}
                  maxFrameWidth={posterMaxWidth}
                  fit="cover"
                  headingClassName={styles.fieldTitle}
                />
              )}

              <div className={styles.footer}>
                <Button
                  title={microreelsListText.cancel}
                  onClick={onCancel}
                  uiType="text"
                />
                <Button
                  title={microreelsListText.save}
                  onClick={() => onSave(title, description, cover)}
                  uiType="action"
                  isLoading={isLoading}
                  disabled={!title}
                />
              </div>
            </>
          )}
        </div>
      </ModalContainer>
    );
  },
);

export default EditMicroreelInfoModal;
